import MovementStatus, { movementState } from './MovementStatus'
import { useEffect, useState } from 'react'
import { Check, Copy, ExternalLink, FileText, Paperclip } from 'lucide-react'
import Modal from './Modal'
import { abrirAnexo, listarAnexos, type Anexo } from '../services/drive'
import { obterBaseAtiva } from '../services/financeiro'
import { formatarCompetenciaBR } from '../utils/formatters'
import type { Categoria, Conta, Lancamento, Subcategoria } from '../types/database'

const brl = new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' })

const dataBR = (valor?: string | null) => {
  if (!valor) return '—'
  const p = valor.slice(0, 10).split('-')
  return p.length === 3 ? `${p[2]}/${p[1]}/${p[0]}` : valor
}

const somenteNumeros = (valor?: string | null) => String(valor || '').replace(/\D/g, '')

type Props = {
  lancamento: Lancamento | null
  categorias: Categoria[]
  subcategorias: Subcategoria[]
  contas: Conta[]
  podeEditar?: boolean
  onClose: () => void
  onEditar?: (lancamento: Lancamento) => void
  onErro?: (mensagem: string) => void
}

const painel: React.CSSProperties = {
  border: '1px solid var(--border)',
  borderRadius: 14,
  padding: 16,
  background: 'var(--surface)',
}

const grade: React.CSSProperties = {
  display: 'grid',
  gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))',
  gap: 12,
}

const campo: React.CSSProperties = {
  minWidth: 0,
  display: 'flex',
  flexDirection: 'column',
  gap: 3,
}

const rotulo: React.CSSProperties = {
  fontSize: 'var(--type-micro)',
  color: 'var(--muted)',
  fontWeight: 'var(--weight-semibold)',
  textTransform: 'uppercase',
  letterSpacing: '.045em',
}

const valor: React.CSSProperties = {
  fontSize: 'var(--type-section-title)',
  fontWeight: 'var(--weight-semibold)',
  color: 'var(--text)',
  overflowWrap: 'anywhere',
}

export default function LancamentoDetalhesModal({
  lancamento,
  categorias,
  subcategorias,
  contas,
  podeEditar = false,
  onClose,
  onEditar,
  onErro,
}: Props) {
  const [codigoCopiado, setCodigoCopiado] = useState<string | null>(null)
  const [anexos, setAnexos] = useState<Anexo[]>([])
  const [baseId, setBaseId] = useState('')
  const [carregandoAnexos, setCarregandoAnexos] = useState(false)
  const [abrindoAnexo, setAbrindoAnexo] = useState<string | null>(null)
  const detalhe = lancamento

  useEffect(() => {
    let ativo = true
    const carregar = async () => {
      if (!detalhe?.id) {
        setAnexos([])
        setBaseId('')
        return
      }
      setCarregandoAnexos(true)
      try {
        const base = await obterBaseAtiva()
        if (!base) throw new Error('Base não localizada.')
        const itens = await listarAnexos(base.id, 'LANCAMENTO', detalhe.id)
        if (ativo) {
          setBaseId(base.id)
          setAnexos(itens)
        }
      } catch (e) {
        console.error('Falha ao carregar anexos nos detalhes:', e)
        if (ativo) {
          setBaseId('')
          setAnexos([])
        }
      } finally {
        if (ativo) setCarregandoAnexos(false)
      }
    }
    void carregar()
    return () => { ativo = false }
  }, [detalhe?.id])

  const copiarTexto = async (texto: string, mensagemErro: string) => {
    if (!texto) return
    try {
      if (navigator.clipboard?.writeText) await navigator.clipboard.writeText(texto)
      else {
        const area = document.createElement('textarea')
        area.value = texto
        area.style.position = 'fixed'
        area.style.opacity = '0'
        document.body.appendChild(area)
        area.select()
        document.execCommand('copy')
        area.remove()
      }
      setCodigoCopiado(texto)
      window.setTimeout(() => setCodigoCopiado((atual) => atual === texto ? null : atual), 1800)
    } catch {
      onErro?.(mensagemErro)
    }
  }

  const copiarCodigo = async () => {
    const codigo = somenteNumeros(detalhe?.codigo_barras)
    await copiarTexto(codigo, 'Não foi possível copiar o código do boleto.')
  }

  const copiarPix = async () => {
    const chave = String(detalhe?.chave_pix || '').trim()
    await copiarTexto(chave, 'Não foi possível copiar a chave PIX.')
  }

  const visualizarAnexo = async (anexo: Anexo) => {
    if (!detalhe?.id || !baseId) return
    setAbrindoAnexo(anexo.id)
    try {
      await abrirAnexo(baseId, 'LANCAMENTO', detalhe.id, anexo.id)
    } catch (e) {
      console.error('Falha ao visualizar anexo:', e)
      onErro?.(e instanceof Error ? e.message : 'Não foi possível abrir o arquivo.')
    } finally {
      setAbrindoAnexo(null)
    }
  }

  const contaId = detalhe?.conta_realizada_id || detalhe?.conta_prevista_id
  const conta = contas.find((item) => item.id === contaId)
  const categoria = categorias.find((item) => item.id === detalhe?.categoria_id)
  const subcategoria = subcategorias.find((item) => item.id === detalhe?.subcategoria_id)
  const realizado = detalhe ? detalhe.status !== 'PENDENTE' : false
  const tipo = detalhe?.tipo === 'RECEITA' ? 'Entrada' : 'Saída'
  const codigo = somenteNumeros(detalhe?.codigo_barras)
  const chavePix = String(detalhe?.chave_pix || '').trim()
  const valorExibido = detalhe
    ? Number(realizado ? (detalhe.valor_realizado || detalhe.valor_previsto || 0) : (detalhe.valor_previsto || 0))
    : 0

  return (
    <Modal
      aberto={!!detalhe}
      titulo={detalhe?.descricao || 'Detalhes do lançamento'}
      subtitulo={detalhe ? `${tipo} · ${realizado ? 'Realizado' : 'Previsto'}` : undefined}
      largo
      onClose={onClose}
    >
      {detalhe && (
        <div data-movement-kind={detalhe.tipo==='RECEITA'?'income':'expense'} data-movement-state={movementState(detalhe.status,detalhe.vencimento)} style={{ display: 'grid', gap: 12 }}>
          <section style={{ ...painel, display: 'grid', gridTemplateColumns: 'minmax(0, 1fr) auto', gap: 20, alignItems: 'center' }}>
            <div style={campo}>
              <span style={rotulo}>{realizado ? 'Valor realizado' : 'Valor previsto'}</span>
              <strong className={`privacy-value ${detalhe.tipo === 'RECEITA' ? 'income' : 'expense'}`} style={{ fontSize: 'var(--type-value)', lineHeight: 1.1 }}>
                {brl.format(valorExibido)}
              </strong>
            </div>
            <div style={{ ...campo, alignItems: 'flex-end', textAlign: 'right' }}>
              <span style={rotulo}>Situação</span>
              <MovementStatus kind={detalhe.tipo==='RECEITA'?'income':'expense'} status={detalhe.status} date={detalhe.vencimento}/>
            </div>
          </section>

          <section style={painel}>
            <h3 style={{ margin: '0 0 12px', fontSize: 'var(--type-dialog-title)' }}>Dados do lançamento</h3>
            <div style={grade}>
              <div style={campo}><span style={rotulo}>Categoria</span><strong style={valor}>{categoria?.nome || '—'}</strong></div>
              <div style={campo}><span style={rotulo}>Subcategoria</span><strong style={valor}>{subcategoria?.nome || '—'}</strong></div>
              <div style={campo}><span style={rotulo}>Conta</span><strong style={valor}>{conta ? `${conta.instituicao} · ${conta.nome}` : '—'}</strong></div>
              <div style={campo}><span style={rotulo}>Competência</span><strong style={valor}>{formatarCompetenciaBR(detalhe.competencia)}</strong></div>
              <div style={campo}><span style={rotulo}>Vencimento</span><strong style={valor}>{dataBR(detalhe.vencimento)}</strong></div>
              <div style={campo}><span style={rotulo}>Realização</span><strong style={valor}>{dataBR(detalhe.data_realizacao)}</strong></div>
            </div>

            {detalhe.tipo === 'DESPESA' && (codigo || chavePix) && (
              <div style={{ display: 'grid', gap: 12, marginTop: 14, paddingTop: 14, borderTop: '1px solid var(--border)' }}>
                {codigo && (
                  <div style={campo}>
                    <span style={rotulo}>Código do boleto</span>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8, minWidth: 0 }}>
                      <strong style={{ ...valor, flex: 1 }}>{detalhe.codigo_barras}</strong>
                      <button
                        type="button"
                        className="table-action"
                        title="Copiar código do boleto"
                        aria-label="Copiar código do boleto"
                        onClick={() => void copiarCodigo()}
                      >
                        {codigoCopiado === codigo ? <Check size={15} /> : <Copy size={15} />}
                      </button>
                    </div>
                  </div>
                )}
                {chavePix && (
                  <div style={campo}>
                    <span style={rotulo}>Chave PIX</span>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8, minWidth: 0 }}>
                      <strong style={{ ...valor, flex: 1 }}>{detalhe.chave_pix}</strong>
                      <button
                        type="button"
                        className="table-action"
                        title="Copiar chave PIX"
                        aria-label="Copiar chave PIX"
                        onClick={() => void copiarPix()}
                      >
                        {codigoCopiado === chavePix ? <Check size={15} /> : <Copy size={15} />}
                      </button>
                    </div>
                  </div>
                )}
              </div>
            )}
          </section>

          <section style={painel}>
            <h3 style={{ margin: '0 0 12px', display: 'flex', alignItems: 'center', gap: 8, fontSize: 'var(--type-dialog-title)' }}><Paperclip size={17} /> Comprovantes e anexos</h3>
            {carregandoAnexos ? (
              <div style={{ color: 'var(--muted)' }}>Carregando anexos...</div>
            ) : anexos.length ? (
              <div style={{ display: 'grid', gap: 8 }}>
                {anexos.map((anexo) => (
                  <div key={anexo.id} style={{ display: 'flex', alignItems: 'center', gap: 10, border: '1px solid var(--border)', borderRadius: 10, padding: '10px 12px', minWidth: 0 }}>
                    <FileText size={18} />
                    <div style={{ minWidth: 0, flex: 1 }}>
                      <strong style={{ display: 'block', overflowWrap: 'anywhere', fontSize: 'var(--type-body)' }}>{anexo.nome_arquivo}</strong>
                      <small style={{ color: 'var(--muted)' }}>{anexo.tamanho ? `${(anexo.tamanho / 1024).toFixed(1)} KB` : 'Arquivo anexado'}</small>
                    </div>
                    <button
                      type="button"
                      className="button ghost compact"
                      title="Visualizar arquivo"
                      disabled={!baseId || abrindoAnexo === anexo.id}
                      onClick={() => void visualizarAnexo(anexo)}
                    >
                      <ExternalLink size={15} />
                      <span>{abrindoAnexo === anexo.id ? 'Abrindo...' : 'Visualizar'}</span>
                    </button>
                  </div>
                ))}
              </div>
            ) : (
              <div style={{ color: 'var(--muted)' }}>Nenhum comprovante anexado.</div>
            )}
          </section>

          {detalhe.observacoes && (
            <section style={painel}>
              <h3 style={{ margin: '0 0 8px', fontSize: 'var(--type-dialog-title)' }}>Observações</h3>
              <p style={{ margin: 0, lineHeight: 1.5 }}>{detalhe.observacoes}</p>
            </section>
          )}

          <div className="modal-actions finance-detail-actions" style={{ marginTop: 0 }}>
            <button className="button ghost" onClick={onClose}>Fechar</button>
            {podeEditar && onEditar && (
              <button className="button primary" onClick={() => onEditar(detalhe)}>Editar</button>
            )}
          </div>
        </div>
      )}
    </Modal>
  )
}

